import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import TitleButtons from "../../../components/TitleButtons";
import CaseHistoricoTable, { CaseHistoricoRow } from "./CaseHistoricoTable";

// ====================== Tipos do Acompanhamento ======================
type AcompHistoricoItem = {
  id: string | number;
  status_id: number | string;
  at: string;
  usuario_id?: number | string | null;
  usuario_nome: string | null;
  comentario: string | null;
};

type CaseSolicitanteItem = {
  solicitante_id: number | string;
  solicitante_nome: string | null;
  created_at?: string | null;
  is_principal?: boolean;
};

type AcompReparo = {
  id: string;
  case_ref?: string | null;
  public_token: string;
  equipamento_nome: string | null;
  descricao_problema: string | null;
  prioridade: number | null;
  solicitante_id?: string | number | null;
  solicitante_nome?: string | null;
  empresa_cliente_id: string | null;
  empresa_cliente_razao_social: string | null;
  embarcacao_cliente_id: string | null;
  status_atual_id: string | number | null;
  historico: AcompHistoricoItem[];
  solicitantes_vinculados?: CaseSolicitanteItem[];

  // opcionais vindos do backend, se existirem
  nota_fiscal_entrada?: string | null;
  nf_entrada?: string | null;
  nota_fiscal_saida?: string | null;
  nf_saida?: string | null;
};

type AcompResponse = {
  fonte: string;
  reparo: AcompReparo;
  erro?: string;
};

// ====================== Opções de Status ======================
type StatusOption = {
  id: number;
  nome: string;
  ativo: boolean;
  visivel_cliente: boolean;
  ordem_sugerida: number | null;
  created_at: string;
  empresa_id: number;
};

type StatusOptionsResponse = {
  fonte?: string;
  empresa_id?: number;
  itens?: StatusOption[];
  erro?: string;
};

// ====================== Tipos dos Anexos ======================
type AnexoItem = {
  id: string | number;
  reparo_id?: string | null;
  file_name?: string | null;
  original_name?: string | null;
  mime_type?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  bucket?: string | null;
  key?: string | null;
  s3_bucket?: string | null;
  s3_key?: string | null;
  kind?: string | null;
  visibility?: string | null;
  created_at?: string | null;
  created_by_nome?: string | null;
};

type AnexosResponse = {
  ok?: boolean;
  itens?: AnexoItem[];
  anexo?: AnexoItem[];
  anexos?: AnexoItem[];
  data?: AnexoItem[];
  erro?: string;
};

type LabAnexoKind = "image" | "document" | "audio" | "video";
type LabAnexoVisibility = "interno" | "cliente";

type PresignUploadResponse = {
  ok: true;
  bucket: string;
  key: string;
  uploadUrl: string;
  expiresIn: number;
  mimeType: string;
  originalName: string;
  sizeBytes: number;
  kind: LabAnexoKind;
};

// ====================== Tipos dos Solicitantes / Em cópia ======================
type SolicitanteItem = {
  id: number | string;
  nome: string;
};

type GetSolicitantesResponse = {
  ok?: boolean;
  data?: SolicitanteItem[];
  erro?: string;
};

type GetCaseSolicitantesResponse = {
  ok?: boolean;
  data?: CaseSolicitanteItem[];
  erro?: string;
};

// ====================== UI Auxiliar ======================
const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff",
    color = "#084298",
    border = "#b6d4fe";

  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }

  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }

  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 0,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: 24 }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: 15,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: 14 }}>
      {value}
    </div>
  </div>
);

function Chip({
  label,
  tone = "blue",
}: {
  label: string;
  tone?: "blue" | "green" | "red" | "gray" | "yellow";
}) {
  const map = {
    blue: { bg: "#e7f1ff", bd: "#b6d4fe", tx: "#084298" },
    green: { bg: "#e6ffed", bd: "#badbcc", tx: "#0f5132" },
    red: { bg: "#ffecec", bd: "#f5c2c7", tx: "#842029" },
    gray: { bg: "#f4f4f4", bd: "#dedede", tx: "#333" },
    yellow: { bg: "#fff7e6", bd: "#ffe2ad", tx: "#7a4a00" },
  }[tone];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 0,
        border: `1px solid ${map.bd}`,
        background: map.bg,
        color: map.tx,
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

const ModalOverlay: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 24,
    }}
  >
    {children}
  </div>
);

const NfCard: React.FC<{
  title: string;
  value: string;
  draft: string;
  editing: boolean;
  saving: boolean;
  onChangeDraft: (v: string) => void;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({
  title,
  value,
  draft,
  editing,
  saving,
  onChangeDraft,
  onStartEdit,
  onCancel,
  onSave,
}) => {
  const hasValue = !!String(value || "").trim();

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        padding: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: editing ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "Inter",
              fontWeight: 800,
              fontSize: 15,
              marginBottom: 10,
            }}
          >
            {title}
          </div>

          {!editing ? (
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: 14,
                minHeight: 22,
              }}
            >
              {hasValue ? value : "—"}
            </div>
          ) : (
            <input
              value={draft}
              onChange={(e) => onChangeDraft(onlyDigits(e.target.value))}
              placeholder="Informe apenas os dígitos da nota fiscal"
              style={{
                width: "100%",
                maxWidth: 420,
                height: 38,
                borderRadius: 0,
                border: "1px solid #d1d5db",
                background: "#fff",
                padding: "0 10px",
                fontFamily: "Inter",
                fontSize: 14,
                outline: "none",
              }}
            />
          )}
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={onStartEdit}
            style={{
              height: 34,
              minWidth: 90,
              borderRadius: 0,
              border: "1px solid black",
              background: "#fff",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            {hasValue ? "Editar" : "Informar"}
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              style={{
                height: 34,
                minWidth: 80,
                borderRadius: 0,
                border: "1px solid black",
                background: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>

            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              style={{
                height: 34,
                minWidth: 90,
                borderRadius: 0,
                border: "1px solid black",
                background: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12,
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ====================== Helpers ======================
function statusToneByName(name?: string | null) {
  const n = String(name ?? "").toLowerCase();
  if (!n) return "gray";
  if (n.includes("final") || n.includes("conclu") || n.includes("entreg"))
    return "green";
  if (n.includes("cancel") || n.includes("reprov")) return "red";
  if (n.includes("aguard") || n.includes("pend") || n.includes("peça"))
    return "yellow";
  if (n.includes("anál") || n.includes("anal") || n.includes("inic"))
    return "blue";
  return "gray";
}

function splitFileNameParts(fileName: string) {
  const trimmed = String(fileName || "").trim();
  const lastDot = trimmed.lastIndexOf(".");

  if (lastDot <= 0 || lastDot === trimmed.length - 1) {
    return {
      baseName: trimmed || "arquivo",
      extension: "",
    };
  }

  return {
    baseName: trimmed.slice(0, lastDot),
    extension: trimmed.slice(lastDot),
  };
}

function sanitizeFileBaseName(name: string) {
  return String(name || "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, " ");
}

function getImageDimensions(file: File): Promise<{
  width: number;
  height: number;
}> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const width = img.naturalWidth || 0;
      const height = img.naturalHeight || 0;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler as dimensões da imagem"));
    };

    img.src = url;
  });
}

function formatDateTimeBR(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso || "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} - ${hh}:${mi}`;
}

function nowIsoLocalish() {
  return new Date().toISOString();
}

function dedupeHistorico(items: AcompHistoricoItem[]) {
  const seen = new Set<string>();
  const out: AcompHistoricoItem[] = [];
  for (const it of items || []) {
    const key = String((it as any).id ?? `${it.status_id}|${it.at}`);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out;
}

function sortHistoricoDesc(items: AcompHistoricoItem[]) {
  const copy = [...(items || [])];
  copy.sort((a, b) => {
    const ta = a.at ? Date.parse(a.at) : 0;
    const tb = b.at ? Date.parse(b.at) : 0;
    return tb - ta;
  });
  return copy;
}

function safeText(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s === "" ? "—" : s;
}

function isUuid(v: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(v || "").trim()
  );
}

function isImageMime(mime?: string | null) {
  const m = String(mime || "").toLowerCase();
  return m.startsWith("image/");
}

function isPdfMime(mime?: string | null) {
  return String(mime || "").toLowerCase() === "application/pdf";
}

function isPdfFile(file: File) {
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

function isImageFile(file: File) {
  const mime = String(file.type || "").toLowerCase();
  return mime.startsWith("image/");
}

function todayBR() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function safeNA(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s ? s : "N/A";
}

function formatBytes(bytes?: number | null) {
  const b = Number(bytes || 0);
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function onlyDigits(v: string) {
  return String(v || "").replace(/\D+/g, "");
}

async function saveBlobAs(blob: Blob, fileName: string) {
  const anyWin = window as any;

  if (anyWin.showSaveFilePicker) {
    const handle = await anyWin.showSaveFilePicker({
      suggestedName: fileName,
      types: [
        {
          description: "Planilha Excel (.xlsx)",
          accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [".xlsx"],
          },
        },
      ],
    });

    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  saveAs(blob, fileName);
}

function xmlEscape(s: string) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

async function gerarXlsxDoModelo(params: {
  caseNumber: string;
  solicitante: string;
  empresa: string;
  dataHoje: string;
  embarcacao: string;
  equipamento: string;
  quantidade: number;
  fileName: string;
}) {
  const res = await fetch("/model.xlsx", { cache: "no-store" });
  const ct = res.headers.get("content-type") || "";
  if (!res.ok) {
    throw new Error(`Falha ao baixar model.xlsx (HTTP ${res.status})`);
  }

  const buf = await res.arrayBuffer();
  const sig = new Uint8Array(buf.slice(0, 2));
  const isZip = sig[0] === 0x50 && sig[1] === 0x4b;

  if (!isZip) {
    const text = new TextDecoder().decode(buf.slice(0, 200));
    throw new Error(
      `model.xlsx não foi servido como arquivo.\nContent-Type: ${ct}\nInício: ${text}`
    );
  }

  const zip = await JSZip.loadAsync(buf);

  const replacements: Record<string, string> = {
    AAAAAAAA: xmlEscape(params.caseNumber),
    BBBBBBBB: xmlEscape(params.solicitante),
    CCCCCCCC: xmlEscape(params.empresa),
    DDDDDDDD: xmlEscape(params.dataHoje),
    EEEEEEEE: xmlEscape(params.embarcacao),
    FFFFFFFF: xmlEscape(params.equipamento),
    GGGGGGGG: xmlEscape(String(params.quantidade)),
  };

  const replaceAll = (input: string) => {
    let out = input;
    for (const [k, v] of Object.entries(replacements)) {
      out = out.split(k).join(v);
    }
    return out;
  };

  const sharedStringsPath = "xl/sharedStrings.xml";
  if (zip.file(sharedStringsPath)) {
    const xml = await zip.file(sharedStringsPath)!.async("string");
    zip.file(sharedStringsPath, replaceAll(xml));
  }

  const sheetFiles = Object.keys(zip.files).filter(
    (p) => p.startsWith("xl/worksheets/") && p.endsWith(".xml")
  );

  for (const p of sheetFiles) {
    const xml = await zip.file(p)!.async("string");
    zip.file(p, replaceAll(xml));
  }

  const extraXmlFiles = Object.keys(zip.files).filter(
    (p) =>
      p.startsWith("xl/") &&
      p.endsWith(".xml") &&
      !p.startsWith("xl/worksheets/") &&
      p !== "xl/sharedStrings.xml"
  );

  for (const p of extraXmlFiles) {
    const xml = await zip.file(p)!.async("string");
    if (
      xml.includes("AAAAAAAA") ||
      xml.includes("BBBBBBBB") ||
      xml.includes("CCCCCCCC") ||
      xml.includes("DDDDDDDD") ||
      xml.includes("EEEEEEEE") ||
      xml.includes("FFFFFFFF") ||
      xml.includes("GGGGGGGG")
    ) {
      zip.file(p, replaceAll(xml));
    }
  }

  const outBuf = await zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
  });

  const blob = new Blob([outBuf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  await saveBlobAs(blob, params.fileName);
}

// ====================== API ======================
async function postExcluirStatusCase(params: {
  historico_id: string | number;
  reparo_id?: string | null;
  meta_json?: any;
}) {
  const url =
    "https://api.logvi-havox.com/departamentos/laboratorio/gestao-de-case/excluir-case-status-id";

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      historico_id: params.historico_id,
      reparo_id: params.reparo_id ?? null,
      meta_json: params.meta_json ?? {},
    }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = (data as any)?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

async function getAcompanhamentoByPublicToken(
  publicToken: string
): Promise<AcompResponse> {
  const url =
    `https://api.logvi-havox.com/departamentos/laboratorio/acompanhamento/get-status-reparos-id` +
    `?public_token=${encodeURIComponent(publicToken)}`;

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const data = (await res.json().catch(() => null)) as AcompResponse | null;

  if (!res.ok) {
    const msg = data?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }

  if (!data || !data.reparo) {
    throw new Error("Resposta inválida da API (sem reparo).");
  }

  return data;
}

async function getOpcoesStatusLaboratorio(): Promise<StatusOption[]> {
  const url =
    "https://api.logvi-havox.com/departamentos/laboratorio/acompanhamento/get-cases-laboratorio-opcoes-status";

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const data = (await res
    .json()
    .catch(() => null)) as StatusOptionsResponse | null;

  if (!res.ok) {
    const msg = data?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }

  const itens = data?.itens;
  const itensSeguro: StatusOption[] = Array.isArray(itens) ? itens : [];

  const ordered = [...itensSeguro].sort((a, b) => {
    const oa = a.ordem_sugerida ?? 999999;
    const ob = b.ordem_sugerida ?? 999999;
    if (oa !== ob) return oa - ob;
    return String(a.nome).localeCompare(String(b.nome), "pt-BR", {
      sensitivity: "base",
    });
  });

  return ordered.map((o) => ({ ...o, id: Number(o.id) }));
}

async function postAdicionarStatusCase(params: {
  reparo_id: string;
  status_id: number;
  comentario: string | null;
  meta_json: any;
}) {
  const url =
    "https://api.logvi-havox.com/departamentos/laboratorio/gestao-de-case/adicionar-case-status-id";

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      reparo_id: params.reparo_id,
      status_id: params.status_id,
      comentario: params.comentario,
      meta_json: params.meta_json ?? {},
    }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = (data as any)?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data;
}

// ====================== API DE ANEXOS ======================
async function presignUploadAnexo(params: {
  reparo_id: string;
  file: File;
  customFileName?: string;
  kind?: LabAnexoKind;
  visibility?: LabAnexoVisibility;
}) {
  const kind = params.kind || "image";
  const visibility = params.visibility || "interno";

  const originalName = params.customFileName || params.file.name;
  const mimeType =
    params.file.type ||
    (String(originalName).toLowerCase().endsWith(".pdf")
      ? "application/pdf"
      : "application/octet-stream");

  const res = await fetch(
    "https://api.logvi-havox.com/departamentos/laboratorio/anexos/presign-upload",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        reparo_id: params.reparo_id,
        original_name: originalName,
        mime_type: mimeType,
        size_bytes: Number(params.file.size || 0),
        kind,
        visibility,
      }),
    }
  );

  const data = await res.json().catch(() => null);

  if (!res.ok || !data?.ok) {
    throw new Error(data?.erro || `Erro ao gerar presign (${res.status})`);
  }

  return data as PresignUploadResponse;
}

async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  forcedMime?: string
) {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": forcedMime || file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Falha no upload ao S3 (${res.status}) ${txt}`);
  }

  return true;
}

async function registrarAnexoCase(params: {
  reparo_id: string;
  s3_bucket: string;
  s3_key: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  kind: string;
  visibility: string;
  width_px?: number | null;
  height_px?: number | null;
}) {
  const res = await fetch(
    "https://api.logvi-havox.com/departamentos/laboratorio/anexos/registrar",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(params),
    }
  );

  const raw = await res.text();
  let data: any = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = { raw };
  }

  if (!res.ok || !data?.ok) {
    throw new Error(
      data?.erro ||
        data?.detail ||
        data?.constraint ||
        data?.raw ||
        `Erro ao registrar anexo (${res.status})`
    );
  }

  return data;
}

async function getAnexosPorReparo(reparoId: string): Promise<AnexoItem[]> {
  const res = await fetch(
    `https://api.logvi-havox.com/departamentos/laboratorio/anexos?reparo_id=${encodeURIComponent(
      reparoId
    )}`,
    {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    }
  );

  const data = (await res.json().catch(() => null)) as AnexosResponse | null;

  if (!res.ok) {
    throw new Error(data?.erro || `Erro ao listar anexos (${res.status})`);
  }

  const itens = data?.itens;
  if (Array.isArray(itens)) return itens;

  const anexos = data?.anexos;
  if (Array.isArray(anexos)) return anexos;

  const anexo = data?.anexo;
  if (Array.isArray(anexo)) return anexo;

  const dataArray = data?.data;
  if (Array.isArray(dataArray)) return dataArray;

  return [];
}

async function getAnexoSignedUrl(anexoId: string | number) {
  const res = await fetch(
    `https://api.logvi-havox.com/departamentos/laboratorio/anexos/${encodeURIComponent(
      String(anexoId)
    )}/url`,
    {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    }
  );

  const raw = await res.text();
  let data: any = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = { raw };
  }

  if (!res.ok) {
    throw new Error(
      data?.erro ||
        data?.detail ||
        data?.raw ||
        `Erro ao obter URL do anexo (${res.status})`
    );
  }

  const url =
    data?.url ||
    data?.downloadUrl ||
    data?.signedUrl ||
    data?.data?.url ||
    data?.data?.signedUrl ||
    data?.data?.downloadUrl ||
    "";

  if (!url) {
    throw new Error("Resposta sem URL assinada do anexo.");
  }

  return url;
}

// ====================== API - Solicitantes / Em cópia ======================
async function getSolicitantesLista(q?: string): Promise<SolicitanteItem[]> {
  const url =
    `https://api.logvi-havox.com/departamentos/laboratorio/solicitantes?limit=10000&order=asc` +
    (q ? `&q=${encodeURIComponent(q)}` : "");

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const data = (await res
    .json()
    .catch(() => null)) as GetSolicitantesResponse | null;

  if (!res.ok) {
    throw new Error(data?.erro || `Erro ao listar pessoas (${res.status})`);
  }

  return Array.isArray(data?.data) ? data!.data! : [];
}

async function getCaseSolicitantes(
  reparoId: string
): Promise<CaseSolicitanteItem[]> {
  const res = await fetch(
    `https://api.logvi-havox.com/departamentos/laboratorio/case-solicitantes?reparo_id=${encodeURIComponent(
      reparoId
    )}`,
    {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    }
  );

  const data = (await res
    .json()
    .catch(() => null)) as GetCaseSolicitantesResponse | null;

  if (!res.ok) {
    throw new Error(data?.erro || `Erro ao listar vinculados (${res.status})`);
  }

  return Array.isArray(data?.data) ? data!.data! : [];
}

async function postAdicionarCaseSolicitante(params: {
  reparo_id: string;
  solicitante_id: number;
}) {
  const res = await fetch(
    "https://api.logvi-havox.com/departamentos/laboratorio/case-solicitantes/adicionar",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(params),
    }
  );

  const data = await res.json().catch(() => null);

  if (!res.ok || !(data as any)?.ok) {
    throw new Error(
      (data as any)?.erro || `Erro ao adicionar pessoa (${res.status})`
    );
  }

  return data;
}

async function postRemoverCaseSolicitante(params: {
  reparo_id: string;
  solicitante_id: number;
}) {
  const res = await fetch(
    "https://api.logvi-havox.com/departamentos/laboratorio/case-solicitantes/remover",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(params),
    }
  );

  const data = await res.json().catch(() => null);

  if (!res.ok || !(data as any)?.ok) {
    throw new Error(
      (data as any)?.erro || `Erro ao remover pessoa (${res.status})`
    );
  }

  return data;
}

// ====================== Draft Row ======================
type DraftNewStatus = {
  status_id: number | "";
  at_iso: string;
  usuario_nome: string;
  comentario: string;
};

// ====================== Página ======================
const LaboratorioDetalhesCase: React.FC = () => {
  const { codigo: codigoParam } = useParams<{ codigo: string }>();
  const navigate = useNavigate();

  const rawCodigo = decodeURIComponent(codigoParam ?? "").trim();
  const publicToken = rawCodigo;

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<AcompResponse | null>(null);

  const [uiRows, setUiRows] = React.useState<CaseHistoricoRow[]>([]);

  const [statusOptions, setStatusOptions] = React.useState<StatusOption[]>([]);
  const [statusOptError, setStatusOptError] = React.useState<string | null>(
    null
  );

  const [draft, setDraft] = React.useState<DraftNewStatus | null>(null);

  const [savingStatus, setSavingStatus] = React.useState(false);
  const [saveStatusError, setSaveStatusError] = React.useState<string | null>(
    null
  );

  const draftRowId = "draft_new_status_row";
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = React.useState<string | null>(null);
  const [uploadSyncWarning, setUploadSyncWarning] = React.useState<
    string | null
  >(null);

  const [anexos, setAnexos] = React.useState<AnexoItem[]>([]);
  const [anexosLoading, setAnexosLoading] = React.useState(false);
  const [anexosError, setAnexosError] = React.useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = React.useState<Record<string, string>>(
    {}
  );

  // modal nome arquivo
  const [selectedUploadFile, setSelectedUploadFile] =
    React.useState<File | null>(null);
  const [fileNameModalOpen, setFileNameModalOpen] = React.useState(false);
  const [fileNameDraft, setFileNameDraft] = React.useState("");
  const [fileNameExtension, setFileNameExtension] = React.useState("");

  // NF entrada / saída
  const [nfEntrada, setNfEntrada] = React.useState("");
  const [nfEntradaDraft, setNfEntradaDraft] = React.useState("");
  const [nfEntradaEditing, setNfEntradaEditing] = React.useState(false);
  const [nfEntradaSaving, setNfEntradaSaving] = React.useState(false);

  const [nfSaida, setNfSaida] = React.useState("");
  const [nfSaidaDraft, setNfSaidaDraft] = React.useState("");
  const [nfSaidaEditing, setNfSaidaEditing] = React.useState(false);
  const [nfSaidaSaving, setNfSaidaSaving] = React.useState(false);

  const [nfMessage, setNfMessage] = React.useState<string | null>(null);
  const [nfError, setNfError] = React.useState<string | null>(null);

  // em cópia / solicitantes
  const [caseSolicitantes, setCaseSolicitantes] = React.useState<
    CaseSolicitanteItem[]
  >([]);
  const [caseSolicitantesLoading, setCaseSolicitantesLoading] =
    React.useState(false);
  const [caseSolicitantesError, setCaseSolicitantesError] = React.useState<
    string | null
  >(null);

  const [copiaModalOpen, setCopiaModalOpen] = React.useState(false);
  const [solicitantesLista, setSolicitantesLista] = React.useState<
    SolicitanteItem[]
  >([]);
  const [solicitantesLoading, setSolicitantesLoading] = React.useState(false);
  const [solicitantesError, setSolicitantesError] = React.useState<
    string | null
  >(null);
  const [solicitanteBusca, setSolicitanteBusca] = React.useState("");
  const [solicitanteSelecionadoId, setSolicitanteSelecionadoId] =
    React.useState<number | "">("");
  const [salvandoCopia, setSalvandoCopia] = React.useState(false);
  const [removendoSolicitanteId, setRemovendoSolicitanteId] = React.useState<
    number | string | null
  >(null);
  const [copiaMessage, setCopiaMessage] = React.useState<string | null>(null);
  const [copiaError, setCopiaError] = React.useState<string | null>(null);

  const statusNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const o of statusOptions || []) {
      const k = String(Number((o as any).id));
      m.set(k, o.nome);
    }
    return (id: number | string | null | undefined) => {
      if (id === null || id === undefined) return "—";
      const k = String(Number(id));
      return m.get(k) ?? `Status ${k}`;
    };
  }, [statusOptions]);

  React.useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        setStatusOptError(null);
        const opts = await getOpcoesStatusLaboratorio();
        if (!disposed) setStatusOptions(opts);
      } catch (e: any) {
        if (!disposed) {
          setStatusOptError(e?.message || "Falha ao carregar opções de status");
        }
      }
    })();
    return () => {
      disposed = true;
    };
  }, []);

  React.useEffect(() => {
    if (!publicToken) return;

    if (!isUuid(publicToken)) {
      setLoading(false);
      setData(null);
      setError("Código inválido (public_token).");
      return;
    }

    let disposed = false;

    (async () => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const resp = await getAcompanhamentoByPublicToken(publicToken);
        if (disposed) return;
        setData(resp);
      } catch (e: any) {
        if (!disposed) setError(e?.message || "Falha ao buscar case");
      } finally {
        if (!disposed) setLoading(false);
      }
    })();

    return () => {
      disposed = true;
    };
  }, [publicToken]);

  React.useEffect(() => {
    const reparo = data?.reparo;
    if (!reparo) return;

    const historicoRaw = reparo.historico || [];
    const historico = sortHistoricoDesc(dedupeHistorico(historicoRaw));

    const rowsFromBackend: CaseHistoricoRow[] = historico.map((h) => {
      const label = statusNameById(h.status_id);
      const histId = (h as any).id;

      return {
        id: `hist_${String(histId)}`,
        __kind: "real",
        __hist_id: histId,
        __reparo_id: reparo.id,
        locked: false,
        cells: [
          <Chip label={label} tone={statusToneByName(label) as any} />,
          formatDateTimeBR(h.at),
          h.usuario_nome == null ? "—" : String(h.usuario_nome),
          safeText(h.comentario),
        ],
      };
    });

    setUiRows((prev) => {
      const prevLocks = new Map<string, boolean>();
      for (const r of prev || []) {
        if ((r as any).__kind === "draft") continue;
        const key = String((r as any).__hist_id ?? r.id);
        prevLocks.set(key, !!r.locked);
      }

      const merged = rowsFromBackend.map((r) => {
        const key = String((r as any).__hist_id ?? r.id);
        const locked = prevLocks.has(key) ? prevLocks.get(key)! : true;
        return { ...r, locked };
      });

      const draftRow = prev.find((r) => r.id === draftRowId);
      return draftRow ? [draftRow, ...merged] : merged;
    });
  }, [data, statusNameById]);

  React.useEffect(() => {
    const reparo = data?.reparo;
    if (!reparo) return;

    const entrada =
      safeText((reparo as any).nota_fiscal_entrada) !== "—"
        ? String((reparo as any).nota_fiscal_entrada || "")
        : safeText((reparo as any).nf_entrada) !== "—"
        ? String((reparo as any).nf_entrada || "")
        : "";

    const saida =
      safeText((reparo as any).nota_fiscal_saida) !== "—"
        ? String((reparo as any).nota_fiscal_saida || "")
        : safeText((reparo as any).nf_saida) !== "—"
        ? String((reparo as any).nf_saida || "")
        : "";

    setNfEntrada(onlyDigits(entrada));
    setNfEntradaDraft(onlyDigits(entrada));
    setNfSaida(onlyDigits(saida));
    setNfSaidaDraft(onlyDigits(saida));
  }, [data]);

  const reparo = data?.reparo || null;

  const loadAnexos = React.useCallback(async () => {
    if (!reparo?.id) return;

    try {
      setAnexosLoading(true);
      setAnexosError(null);

      const itens = await getAnexosPorReparo(reparo.id);
      setAnexos(itens);

      const imagens = itens.filter((x) =>
        isImageMime(x.mime_type || x.content_type || "")
      );

      const nextPreviewMap: Record<string, string> = {};
      const previewErrors: string[] = [];

      await Promise.all(
        imagens.map(async (img) => {
          try {
            const url = await getAnexoSignedUrl(img.id);
            nextPreviewMap[String(img.id)] = url;
          } catch (e: any) {
            previewErrors.push(
              `Imagem ${img.id}: ${e?.message || "falha ao obter URL assinada"}`
            );
          }
        })
      );

      setPreviewUrls((prev) => ({ ...prev, ...nextPreviewMap }));

      if (previewErrors.length > 0) {
        console.error("Erros de preview dos anexos:", previewErrors);
        setAnexosError(
          `Alguns previews não puderam ser carregados. Ex.: ${previewErrors[0]}`
        );
      }
    } catch (e: any) {
      setAnexosError(e?.message || "Falha ao carregar anexos");
    } finally {
      setAnexosLoading(false);
    }
  }, [reparo?.id]);

  const loadCaseSolicitantes = React.useCallback(async () => {
    if (!reparo?.id) return;

    try {
      setCaseSolicitantesLoading(true);
      setCaseSolicitantesError(null);

      const itens =
        Array.isArray(reparo?.solicitantes_vinculados) &&
        reparo.solicitantes_vinculados.length
          ? reparo.solicitantes_vinculados
          : await getCaseSolicitantes(reparo.id);

      setCaseSolicitantes(itens || []);
    } catch (e: any) {
      setCaseSolicitantesError(
        e?.message || "Falha ao carregar pessoas vinculadas"
      );
    } finally {
      setCaseSolicitantesLoading(false);
    }
  }, [reparo?.id, reparo?.solicitantes_vinculados]);

  const loadSolicitantes = React.useCallback(async (q?: string) => {
    try {
      setSolicitantesLoading(true);
      setSolicitantesError(null);

      const itens = await getSolicitantesLista(q);
      setSolicitantesLista(itens || []);
    } catch (e: any) {
      setSolicitantesError(e?.message || "Falha ao carregar lista de pessoas");
    } finally {
      setSolicitantesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!reparo?.id) return;
    loadAnexos();
  }, [reparo?.id, loadAnexos]);

  React.useEffect(() => {
    if (!reparo?.id) return;
    loadCaseSolicitantes();
  }, [reparo?.id, loadCaseSolicitantes]);

  if (!publicToken) {
    return <Navigate to="/laboratorio/gestao-de-cases" replace />;
  }

  const headerCode =
    safeText(reparo?.case_ref) !== "—"
      ? safeText(reparo?.case_ref)
      : publicToken;

  const equipamentoNome = loading
    ? "Carregando..."
    : error
    ? "—"
    : reparo?.equipamento_nome || "—";

  const descricaoProblema = loading
    ? "Carregando..."
    : error
    ? "—"
    : reparo?.descricao_problema || "—";

  const statusAtualLabel = loading
    ? "Carregando..."
    : error
    ? "—"
    : statusNameById(reparo?.status_atual_id ?? null);

  const currentUserName =
    (uiRows
      .map((r) => String(r.cells?.[2] ?? "").trim())
      .find((s) => s && s !== "—") as string) || "Você";

  const imagensAnexadas = anexos.filter((x) =>
    isImageMime(x.mime_type || x.content_type || "")
  );

  const pdfsAnexados = anexos.filter(
    (x) =>
      isPdfMime(x.mime_type || x.content_type || "") ||
      ["document"].includes(String(x.kind || "").toLowerCase()) ||
      String(x.file_name || x.original_name || "")
        .toLowerCase()
        .endsWith(".pdf")
  );

  const handleGerarXlsx = async () => {
    try {
      const r = data?.reparo;

      const caseNumber = safeNA(r?.case_ref);
      const solicitante = safeNA(r?.solicitante_nome);
      const empresa = safeNA(r?.empresa_cliente_razao_social);
      const dataHoje = todayBR();
      const embarcacao = safeNA((r as any)?.embarcacao_cliente_nome);
      const equipamento = safeNA(r?.equipamento_nome);
      const quantidade = 1;

      const fileName = `SAIDA-${
        caseNumber !== "N/A" ? caseNumber : headerCode
      }.xlsx`;

      await gerarXlsxDoModelo({
        caseNumber,
        solicitante,
        empresa,
        dataHoje,
        embarcacao,
        equipamento,
        quantidade,
        fileName,
      });
    } catch (e: any) {
      alert(e?.message || "Falha ao gerar XLSX.");
    }
  };

  const openDraft = () => {
    if (draft) return;
    if (!statusOptions.length) {
      alert("Aguarde: carregando opções de status...");
      return;
    }

    const firstOptId = statusOptions?.[0]?.id ?? "";

    const nextDraft: DraftNewStatus = {
      status_id: firstOptId,
      at_iso: nowIsoLocalish(),
      usuario_nome: currentUserName,
      comentario: "N/I",
    };

    setSaveStatusError(null);
    setDraft(nextDraft);

    setUiRows((prev) => {
      const noDraft = prev.filter((r) => r.id !== draftRowId);
      const draftRow: CaseHistoricoRow = {
        id: draftRowId,
        locked: true,
        __kind: "draft",
        cells: [],
      };
      return [draftRow, ...noDraft];
    });
  };

  const draftCells = (() => {
    if (!draft) return null;

    const selectStyle: React.CSSProperties = {
      width: "100%",
      height: 30,
      borderRadius: 0,
      border: "1px solid #d1d5db",
      background: "#fff",
      padding: "0 8px",
      fontFamily: "Inter",
      fontSize: 13,
      outline: "none",
    };

    const inputStyle: React.CSSProperties = {
      width: "90%",
      height: 30,
      borderRadius: 0,
      border: "1px solid #d1d5db",
      background: "#fff",
      padding: "0 8px",
      fontFamily: "Inter",
      fontSize: 13,
      outline: "none",
    };

    return [
      <select
        value={draft.status_id}
        onChange={(e) => {
          const v = e.target.value;
          setDraft((d) =>
            d ? { ...d, status_id: v === "" ? "" : Number(v) } : d
          );
        }}
        style={selectStyle}
        disabled={savingStatus}
      >
        {statusOptions.length === 0 ? (
          <option value="">Carregando…</option>
        ) : (
          <>
            <option value="">Selecione…</option>
            {statusOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nome}
              </option>
            ))}
          </>
        )}
      </select>,

      <span style={{ fontFamily: "Inter", fontSize: 13 }}>
        {formatDateTimeBR(draft.at_iso)}
      </span>,

      <span style={{ fontFamily: "Inter", fontSize: 13 }}>
        {draft.usuario_nome}
      </span>,

      <input
        value={draft.comentario}
        disabled={savingStatus}
        onChange={(e) =>
          setDraft((d) => (d ? { ...d, comentario: e.target.value } : d))
        }
        placeholder="Digite um comentário..."
        style={inputStyle}
      />,
    ] as any;
  })();

  const cancelDraft = () => {
    if (savingStatus) return;
    setDraft(null);
    setUiRows((prev) => prev.filter((r) => r.id !== draftRowId));
  };

  const confirmDraft = async () => {
    if (!draft) return;

    setSaveStatusError(null);

    const status_id = draft.status_id;
    if (status_id === "" || !Number.isFinite(Number(status_id))) {
      alert("Selecione um status.");
      return;
    }

    if (!reparo?.id) {
      alert("Não foi possível identificar o reparo_id deste case.");
      return;
    }

    try {
      setSavingStatus(true);

      const resp = await postAdicionarStatusCase({
        reparo_id: reparo.id,
        status_id: Number(status_id),
        comentario: String(draft.comentario ?? "").trim() || null,
        meta_json: {},
      });

      const newHistId =
        (resp as any)?.data?.historico?.id ??
        (resp as any)?.historico?.id ??
        null;

      const statusName = statusNameById(Number(status_id));
      const atIso = nowIsoLocalish();
      const user = draft.usuario_nome || currentUserName;

      const newRow: CaseHistoricoRow = {
        id:
          newHistId != null
            ? `hist_${String(newHistId)}`
            : `hist_${Date.now()}`,
        __kind: "real",
        __hist_id: newHistId,
        __reparo_id: reparo.id,
        locked: true,
        cells: [
          <Chip
            label={statusName}
            tone={statusToneByName(statusName) as any}
          />,
          formatDateTimeBR(atIso),
          user,
          safeText(draft.comentario),
        ],
      };

      setUiRows((prev) => {
        const withoutDraft = prev.filter((r) => r.id !== draftRowId);
        return [newRow, ...withoutDraft];
      });

      setDraft(null);

      try {
        const fresh = await getAcompanhamentoByPublicToken(publicToken);
        setData(fresh);
      } catch {
        //
      }
    } catch (e: any) {
      setSaveStatusError(e?.message || "Falha ao salvar status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteRowServer = async (row: CaseHistoricoRow) => {
    if ((row as any).__kind === "draft") return;

    const historico_id = (row as any).__hist_id;
    if (historico_id == null || String(historico_id).trim() === "") {
      throw new Error(
        "Não foi possível identificar o historico_id desta linha."
      );
    }

    const reparo_id = (row as any).__reparo_id ?? reparo?.id ?? null;

    await postExcluirStatusCase({ historico_id, reparo_id, meta_json: {} });

    try {
      const fresh = await getAcompanhamentoByPublicToken(publicToken);
      setData(fresh);
    } catch {
      //
    }
  };

  const renderRowActions = (row: CaseHistoricoRow) => {
    if ((row as any).__kind === "draft") {
      const borderColor = "black";
      const btnBase: React.CSSProperties = {
        height: 28,
        minWidth: 86,
        borderRadius: 0,
        border: `1px solid ${borderColor}`,
        background: "#fff",
        cursor: "pointer",
        fontSize: 12,
      };

      const btnSmall: React.CSSProperties = { ...btnBase, minWidth: 64 };

      const disabledAdd =
        savingStatus ||
        !draft ||
        draft.status_id === "" ||
        !Number.isFinite(Number(draft.status_id));

      return (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <button
            type="button"
            onClick={confirmDraft}
            disabled={disabledAdd}
            style={{
              ...btnBase,
              border: `1px solid ${disabledAdd ? "#d1d5db" : borderColor}`,
              background: disabledAdd ? "#f3f4f6" : "#fff",
              cursor: disabledAdd ? "not-allowed" : "pointer",
              opacity: disabledAdd ? 0.6 : 1,
            }}
          >
            {savingStatus ? "Salvando..." : "Adicionar"}
          </button>

          <button
            type="button"
            onClick={cancelDraft}
            disabled={savingStatus}
            style={{
              ...btnSmall,
              opacity: savingStatus ? 0.6 : 1,
              cursor: savingStatus ? "not-allowed" : "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      );
    }
    return null;
  };

  const handleClickAnexarImagem = () => {
    if (!reparo?.id) {
      alert("O reparo ainda não foi carregado.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleSelectImage: React.ChangeEventHandler<HTMLInputElement> = async (
    e
  ) => {
    const file = e.target.files?.[0] || null;
    e.currentTarget.value = "";

    if (!file) return;

    if (!reparo?.id) {
      alert("Não foi possível identificar o reparo.");
      return;
    }

    const image = isImageFile(file);
    const pdf = isPdfFile(file);

    if (!image && !pdf) {
      alert("Selecione apenas imagem ou PDF.");
      return;
    }

    const { baseName, extension } = splitFileNameParts(file.name);
    setSelectedUploadFile(file);
    setFileNameDraft(baseName);
    setFileNameExtension(extension);
    setFileNameModalOpen(true);
  };

  const closeFileNameModal = () => {
    if (uploadingImage) return;
    setFileNameModalOpen(false);
    setSelectedUploadFile(null);
    setFileNameDraft("");
    setFileNameExtension("");
  };

  const confirmUploadWithCustomName = async () => {
    const file = selectedUploadFile;
    if (!file || !reparo?.id) return;

    const cleanBaseName = sanitizeFileBaseName(fileNameDraft);

    if (!cleanBaseName) {
      alert("Informe um nome de arquivo válido.");
      return;
    }

    const image = isImageFile(file);
    const pdf = isPdfFile(file);

    if (!image && !pdf) {
      alert("Selecione apenas imagem ou PDF.");
      return;
    }

    const finalFileName = `${cleanBaseName}${fileNameExtension || ""}`;
    const resolvedMime =
      file.type || (pdf ? "application/pdf" : "application/octet-stream");

    setUploadingImage(true);
    setUploadError(null);
    setUploadSuccess(null);
    setUploadSyncWarning(null);

    let presign: PresignUploadResponse | null = null;

    try {
      const dims = image
        ? await getImageDimensions(file).catch(() => null)
        : null;

      presign = await presignUploadAnexo({
        reparo_id: reparo.id,
        file,
        customFileName: finalFileName,
        kind: image ? "image" : "document",
        visibility: "interno",
      });

      await uploadFileToS3(presign.uploadUrl, file, resolvedMime);

      await registrarAnexoCase({
        reparo_id: reparo.id,
        s3_bucket: presign.bucket,
        s3_key: presign.key,
        original_name: finalFileName,
        mime_type: presign.mimeType || resolvedMime,
        size_bytes: Number(presign.sizeBytes || file.size || 0),
        kind: image ? "image" : "document",
        visibility: "interno",
        width_px: image ? dims?.width ?? null : null,
        height_px: image ? dims?.height ?? null : null,
      });

      setUploadSuccess(
        `${image ? "Imagem" : "PDF"} "${finalFileName}" enviado com sucesso.`
      );

      setFileNameModalOpen(false);
      setSelectedUploadFile(null);
      setFileNameDraft("");
      setFileNameExtension("");

      try {
        await loadAnexos();
      } catch (loadErr: any) {
        setUploadSyncWarning(
          loadErr?.message ||
            "O arquivo foi registrado, mas não foi possível atualizar a lista agora."
        );
      }
    } catch (err: any) {
      setUploadError(err?.message || "Falha ao anexar arquivo");
      setUploadSuccess(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveNfEntrada = async () => {
    try {
      setNfError(null);
      setNfMessage(null);
      setNfEntradaSaving(true);

      const finalValue = onlyDigits(nfEntradaDraft);
      setNfEntrada(finalValue);
      setNfEntradaDraft(finalValue);
      setNfEntradaEditing(false);
      setNfMessage("Nota Fiscal de entrada atualizada.");
    } catch (e: any) {
      setNfError(e?.message || "Falha ao salvar Nota Fiscal de entrada.");
    } finally {
      setNfEntradaSaving(false);
    }
  };

  const handleSaveNfSaida = async () => {
    try {
      setNfError(null);
      setNfMessage(null);
      setNfSaidaSaving(true);

      const finalValue = onlyDigits(nfSaidaDraft);
      setNfSaida(finalValue);
      setNfSaidaDraft(finalValue);
      setNfSaidaEditing(false);
      setNfMessage("Nota Fiscal de saída atualizada.");
    } catch (e: any) {
      setNfError(e?.message || "Falha ao salvar Nota Fiscal de saída.");
    } finally {
      setNfSaidaSaving(false);
    }
  };

  const openCopiaModal = async () => {
    if (!reparo?.id) {
      alert("O reparo ainda não foi carregado.");
      return;
    }

    setCopiaError(null);
    setCopiaMessage(null);
    setSolicitanteBusca("");
    setSolicitanteSelecionadoId("");
    setCopiaModalOpen(true);

    await loadSolicitantes("");
  };

  const closeCopiaModal = () => {
    if (salvandoCopia) return;
    setCopiaModalOpen(false);
    setSolicitanteBusca("");
    setSolicitanteSelecionadoId("");
  };

  const handleBuscarSolicitantes = async () => {
    await loadSolicitantes(solicitanteBusca);
  };

  const handleAdicionarPessoaEmCopia = async () => {
    if (!reparo?.id) {
      alert("Não foi possível identificar o case.");
      return;
    }

    if (
      solicitanteSelecionadoId === "" ||
      !Number.isFinite(Number(solicitanteSelecionadoId))
    ) {
      alert("Selecione uma pessoa.");
      return;
    }

    try {
      setSalvandoCopia(true);
      setCopiaError(null);
      setCopiaMessage(null);

      await postAdicionarCaseSolicitante({
        reparo_id: reparo.id,
        solicitante_id: Number(solicitanteSelecionadoId),
      });

      await loadCaseSolicitantes();

      const pessoa = solicitantesLista.find(
        (x) => Number(x.id) === Number(solicitanteSelecionadoId)
      );

      setCopiaMessage(
        `Pessoa adicionada ao case${pessoa?.nome ? `: ${pessoa.nome}` : ""}.`
      );

      setCopiaModalOpen(false);
      setSolicitanteBusca("");
      setSolicitanteSelecionadoId("");
    } catch (e: any) {
      setCopiaError(e?.message || "Falha ao adicionar pessoa ao case");
    } finally {
      setSalvandoCopia(false);
    }
  };

  const handleRemoverPessoaEmCopia = async (solicitanteId: number | string) => {
    if (!reparo?.id) return;

    try {
      setRemovendoSolicitanteId(solicitanteId);
      setCopiaError(null);
      setCopiaMessage(null);

      await postRemoverCaseSolicitante({
        reparo_id: reparo.id,
        solicitante_id: Number(solicitanteId),
      });

      await loadCaseSolicitantes();
      setCopiaMessage("Pessoa removida do case com sucesso.");
    } catch (e: any) {
      setCopiaError(e?.message || "Falha ao remover pessoa do case");
    } finally {
      setRemovendoSolicitanteId(null);
    }
  };

  return (
    <div>
      <TitleButtons
        title="Laboratório - Detalhes do Case"
        buttonMarginRight="0px"
        marginLeft="40px"
        outerMarginRight="40px"
        buttons={[
          {
            label: "Voltar",
            onClick: () => navigate("/laboratorio/gestao-de-cases"),
            variant: "secondary",
          },
          {
            label: "Gerar documento de saída",
            onClick: handleGerarXlsx,
            variant: "secondary",
          },
          {
            label: uploadingImage ? "Enviando arquivo..." : "Anexar arquivo",
            onClick: handleClickAnexarImagem,
            variant: "secondary",
          },
          {
            label: "Adicionar em cópia",
            onClick: openCopiaModal,
            variant: "secondary",
          },
          {
            label: "Adicionar novo status",
            onClick: openDraft,
            variant: "primary",
          },
        ]}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={handleSelectImage}
      />

      {fileNameModalOpen && (
        <ModalOverlay>
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: "#fff",
              border: "1px solid #dcdcdc",
              padding: 20,
            }}
          >
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 800,
                fontSize: 18,
                marginBottom: 16,
              }}
            >
              Informar nome do arquivo
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontSize: 13,
                opacity: 0.75,
                marginBottom: 8,
              }}
            >
              Nome do arquivo
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
                marginBottom: 20,
              }}
            >
              <input
                autoFocus
                value={fileNameDraft}
                onChange={(e) => setFileNameDraft(e.target.value)}
                disabled={uploadingImage}
                style={{
                  width: "100%",
                  height: 38,
                  borderRadius: 0,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  padding: "0 10px",
                  fontFamily: "Inter",
                  fontSize: 14,
                  outline: "none",
                }}
              />

              <div
                style={{
                  minWidth: 90,
                  height: 38,
                  border: "1px solid #d1d5db",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 10px",
                  fontFamily: "Inter",
                  fontSize: 14,
                  background: "#f8f8f8",
                }}
              >
                {fileNameExtension || "sem ext"}
              </div>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                type="button"
                onClick={closeFileNameModal}
                disabled={uploadingImage}
                style={{
                  height: 34,
                  minWidth: 90,
                  borderRadius: 0,
                  border: "1px solid black",
                  background: "#fff",
                  cursor: uploadingImage ? "not-allowed" : "pointer",
                  fontSize: 12,
                  opacity: uploadingImage ? 0.6 : 1,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={confirmUploadWithCustomName}
                disabled={uploadingImage}
                style={{
                  height: 34,
                  minWidth: 90,
                  borderRadius: 0,
                  border: "1px solid black",
                  background: "#fff",
                  cursor: uploadingImage ? "not-allowed" : "pointer",
                  fontSize: 12,
                  opacity: uploadingImage ? 0.6 : 1,
                }}
              >
                {uploadingImage ? "Enviando..." : "Salvar"}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {copiaModalOpen && (
        <ModalOverlay>
          <div
            style={{
              width: "100%",
              maxWidth: 620,
              background: "#fff",
              border: "1px solid #dcdcdc",
              padding: 20,
            }}
          >
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 800,
                fontSize: 18,
                marginBottom: 16,
              }}
            >
              Adicionar pessoa em cópia
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <input
                value={solicitanteBusca}
                onChange={(e) => setSolicitanteBusca(e.target.value)}
                placeholder="Buscar pessoa pelo nome"
                disabled={salvandoCopia}
                style={{
                  width: "100%",
                  height: 38,
                  borderRadius: 0,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  padding: "0 10px",
                  fontFamily: "Inter",
                  fontSize: 14,
                  outline: "none",
                }}
              />

              <button
                type="button"
                onClick={handleBuscarSolicitantes}
                disabled={solicitantesLoading || salvandoCopia}
                style={{
                  height: 38,
                  minWidth: 110,
                  borderRadius: 0,
                  border: "1px solid black",
                  background: "#fff",
                  cursor:
                    solicitantesLoading || salvandoCopia
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 12,
                  opacity: solicitantesLoading || salvandoCopia ? 0.6 : 1,
                }}
              >
                {solicitantesLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>

            {solicitantesError && (
              <div style={{ color: "#842029", fontSize: 12, marginBottom: 10 }}>
                {solicitantesError}
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <div
                style={{
                  fontFamily: "Inter",
                  fontSize: 13,
                  opacity: 0.75,
                  marginBottom: 8,
                }}
              >
                Selecionar pessoa
              </div>

              <select
                value={solicitanteSelecionadoId}
                onChange={(e) =>
                  setSolicitanteSelecionadoId(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                disabled={salvandoCopia || solicitantesLoading}
                style={{
                  width: "100%",
                  height: 38,
                  borderRadius: 0,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  padding: "0 10px",
                  fontFamily: "Inter",
                  fontSize: 14,
                  outline: "none",
                }}
              >
                <option value="">Selecione…</option>
                {solicitantesLista
                  .filter(
                    (s) =>
                      !caseSolicitantes.some(
                        (v) => Number(v.solicitante_id) === Number(s.id)
                      )
                  )
                  .map((s) => (
                    <option key={String(s.id)} value={Number(s.id)}>
                      {s.nome}
                    </option>
                  ))}
              </select>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                type="button"
                onClick={closeCopiaModal}
                disabled={salvandoCopia}
                style={{
                  height: 34,
                  minWidth: 90,
                  borderRadius: 0,
                  border: "1px solid black",
                  background: "#fff",
                  cursor: salvandoCopia ? "not-allowed" : "pointer",
                  fontSize: 12,
                  opacity: salvandoCopia ? 0.6 : 1,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleAdicionarPessoaEmCopia}
                disabled={salvandoCopia}
                style={{
                  height: 34,
                  minWidth: 90,
                  borderRadius: 0,
                  border: "1px solid black",
                  background: "#fff",
                  cursor: salvandoCopia ? "not-allowed" : "pointer",
                  fontSize: 12,
                  opacity: salvandoCopia ? 0.6 : 1,
                }}
              >
                {salvandoCopia ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {statusOptError && (
        <NoticeBanner type="notify">
          Não foi possível carregar opções de status:{" "}
          <strong>{statusOptError}</strong>
        </NoticeBanner>
      )}

      {saveStatusError && (
        <NoticeBanner type="error">
          Não foi possível salvar o novo status:{" "}
          <strong>{saveStatusError}</strong>
        </NoticeBanner>
      )}

      {error && (
        <NoticeBanner type="error">
          Erro ao carregar dados do case: <strong>{error}</strong>
        </NoticeBanner>
      )}

      {uploadError && (
        <NoticeBanner type="error">
          Não foi possível anexar o arquivo: <strong>{uploadError}</strong>
        </NoticeBanner>
      )}

      {uploadSuccess && (
        <NoticeBanner type="success">{uploadSuccess}</NoticeBanner>
      )}

      {uploadSyncWarning && (
        <NoticeBanner type="notify">{uploadSyncWarning}</NoticeBanner>
      )}

      {anexosError && (
        <NoticeBanner type="error">
          Não foi possível carregar os anexos: <strong>{anexosError}</strong>
        </NoticeBanner>
      )}

      {nfError && (
        <NoticeBanner type="error">
          Não foi possível salvar a nota fiscal: <strong>{nfError}</strong>
        </NoticeBanner>
      )}

      {nfMessage && <NoticeBanner type="success">{nfMessage}</NoticeBanner>}

      {copiaError && (
        <NoticeBanner type="error">
          Não foi possível atualizar os vinculados do case:{" "}
          <strong>{copiaError}</strong>
        </NoticeBanner>
      )}

      {copiaMessage && (
        <NoticeBanner type="success">{copiaMessage}</NoticeBanner>
      )}

      {caseSolicitantesError && (
        <NoticeBanner type="error">
          Não foi possível carregar as pessoas vinculadas:{" "}
          <strong>{caseSolicitantesError}</strong>
        </NoticeBanner>
      )}

      <div
        style={{
          padding: "24px 40px 0 40px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #e8e8e8",
            borderRadius: 0,
            padding: 16,
          }}
        >
          <div
            style={{
              fontFamily: "Inter",
              fontWeight: 800,
              fontSize: 13,
              opacity: 0.75,
              marginBottom: 10,
            }}
          >
            Identificação
          </div>

          <Field
            label="Número do Case"
            value={
              safeText(reparo?.case_ref) !== "—"
                ? safeText(reparo?.case_ref)
                : "—"
            }
          />

          <Field label="Public Token" value={publicToken} />
          <Field label="Nome do Equipamento" value={equipamentoNome} />
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #e8e8e8",
            borderRadius: 0,
            padding: 16,
          }}
        >
          <div
            style={{
              fontFamily: "Inter",
              fontWeight: 800,
              fontSize: 13,
              opacity: 0.75,
              marginBottom: 10,
            }}
          >
            Situação
          </div>

          <Field label="Descrição do Problema" value={descricaoProblema} />

          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 6,
              }}
            >
              Status Atual
            </div>

            <Chip
              label={statusAtualLabel}
              tone={statusToneByName(statusAtualLabel) as any}
            />
          </div>
        </div>
      </div>

      <div style={{ padding: "16px 40px 0 40px", display: "grid", gap: 16 }}>
        <NfCard
          title="Nota Fiscal de entrada"
          value={nfEntrada}
          draft={nfEntradaDraft}
          editing={nfEntradaEditing}
          saving={nfEntradaSaving}
          onChangeDraft={setNfEntradaDraft}
          onStartEdit={() => {
            setNfError(null);
            setNfMessage(null);
            setNfEntradaDraft(nfEntrada);
            setNfEntradaEditing(true);
          }}
          onCancel={() => {
            setNfEntradaDraft(nfEntrada);
            setNfEntradaEditing(false);
          }}
          onSave={handleSaveNfEntrada}
        />

        <NfCard
          title="Nota Fiscal de saída"
          value={nfSaida}
          draft={nfSaidaDraft}
          editing={nfSaidaEditing}
          saving={nfSaidaSaving}
          onChangeDraft={setNfSaidaDraft}
          onStartEdit={() => {
            setNfError(null);
            setNfMessage(null);
            setNfSaidaDraft(nfSaida);
            setNfSaidaEditing(true);
          }}
          onCancel={() => {
            setNfSaidaDraft(nfSaida);
            setNfSaidaEditing(false);
          }}
          onSave={handleSaveNfSaida}
        />
      </div>

      <div style={{ padding: "16px 40px 0 40px" }}>
        <div
          style={{
            fontFamily: "Inter",
            fontWeight: 800,
            fontSize: 15,
            marginBottom: 10,
          }}
        >
          Anexos
        </div>

        {anexosLoading ? (
          <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
            Carregando anexos…
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 10,
                }}
              >
                Imagens
              </div>

              {imagensAnexadas.length === 0 ? (
                <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
                  Nenhuma imagem anexada para este case.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: 12,
                  }}
                >
                  {imagensAnexadas.map((img) => {
                    const imgUrl = previewUrls[String(img.id)] || "";

                    return (
                      <div
                        key={String(img.id)}
                        style={{
                          background: "#fff",
                          border: "1px solid #e8e8e8",
                          padding: 8,
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            background: "#f7f7f7",
                            border: "1px solid #ececec",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            marginBottom: 8,
                            cursor: imgUrl ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (imgUrl) {
                              window.open(
                                imgUrl,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }
                          }}
                        >
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={
                                img.file_name ||
                                img.original_name ||
                                `Imagem ${img.id}`
                              }
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: 12, opacity: 0.6 }}>
                              Sem preview
                            </span>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            fontFamily: "Inter",
                            fontWeight: 600,
                            wordBreak: "break-word",
                          }}
                        >
                          {img.file_name ||
                            img.original_name ||
                            `Imagem ${img.id}`}
                        </div>

                        <div
                          style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}
                        >
                          {img.created_at
                            ? formatDateTimeBR(img.created_at)
                            : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div
                style={{
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 10,
                }}
              >
                PDFs
              </div>

              {pdfsAnexados.length === 0 ? (
                <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
                  Nenhum PDF anexado para este case.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 10,
                  }}
                >
                  {pdfsAnexados.map((doc) => (
                    <div
                      key={String(doc.id)}
                      style={{
                        background: "#fff",
                        border: "1px solid #e8e8e8",
                        padding: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontFamily: "Inter",
                            fontWeight: 600,
                            wordBreak: "break-word",
                          }}
                        >
                          {doc.file_name ||
                            doc.original_name ||
                            `PDF ${doc.id}`}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            opacity: 0.7,
                            marginTop: 4,
                          }}
                        >
                          {doc.created_at
                            ? formatDateTimeBR(doc.created_at)
                            : "—"}{" "}
                          • {formatBytes(doc.size_bytes)}
                        </div>
                      </div>

                      <button
                        type="button"
                        style={{
                          height: 32,
                          minWidth: 100,
                          borderRadius: 0,
                          border: "1px solid black",
                          background: "#fff",
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                        onClick={async () => {
                          try {
                            const url = await getAnexoSignedUrl(doc.id);
                            window.open(url, "_blank", "noopener,noreferrer");
                          } catch (e: any) {
                            alert(e?.message || "Falha ao abrir PDF.");
                          }
                        }}
                      >
                        Abrir PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ padding: "16px 40px 0 40px" }}>
        <div
          style={{
            fontFamily: "Inter",
            fontWeight: 800,
            fontSize: 15,
            marginBottom: 10,
          }}
        >
          Pessoas vinculadas ao case
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #e8e8e8",
            padding: 16,
          }}
        >
          {caseSolicitantesLoading ? (
            <div style={{ opacity: 0.7, fontSize: 14 }}>
              Carregando pessoas vinculadas…
            </div>
          ) : caseSolicitantes.length === 0 ? (
            <div style={{ opacity: 0.7, fontSize: 14 }}>
              Nenhuma pessoa vinculada a este case.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {caseSolicitantes.map((item) => (
                <div
                  key={String(item.solicitante_id)}
                  style={{
                    border: "1px solid #ececec",
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {item.solicitante_nome ||
                          `Pessoa ${item.solicitante_id}`}
                      </span>
                      {item.is_principal ? (
                        <Chip label="Principal" tone="blue" />
                      ) : (
                        <Chip label="Em cópia" tone="gray" />
                      )}
                    </div>

                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                      {item.created_at
                        ? formatDateTimeBR(item.created_at)
                        : "—"}
                    </div>
                  </div>

                  {!item.is_principal && (
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoverPessoaEmCopia(item.solicitante_id)
                      }
                      disabled={removendoSolicitanteId === item.solicitante_id}
                      style={{
                        height: 32,
                        minWidth: 90,
                        borderRadius: 0,
                        border: "1px solid black",
                        background: "#fff",
                        cursor:
                          removendoSolicitanteId === item.solicitante_id
                            ? "not-allowed"
                            : "pointer",
                        fontSize: 12,
                        opacity:
                          removendoSolicitanteId === item.solicitante_id
                            ? 0.6
                            : 1,
                      }}
                    >
                      {removendoSolicitanteId === item.solicitante_id
                        ? "Removendo..."
                        : "Remover"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "16px 40px 0 40px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{
              fontFamily: "Inter",
              fontWeight: 800,
              fontSize: 15,
              marginBottom: 10,
            }}
          >
            Histórico do Case
          </div>

          <div style={{ fontFamily: "Inter", fontSize: 12, opacity: 0.7 }}>
            {uiRows.length} registro(s)
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
            Carregando histórico…
          </div>
        ) : uiRows.length === 0 ? (
          <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
            Sem histórico para este case.
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: 0,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CaseHistoricoTable
              headers={["Status", "Data", "Usuário", "Comentário"]}
              rows={uiRows}
              onRowsChange={setUiRows}
              softBorders={true}
              dense={false}
              enableZebra={true}
              hoverHighlight={true}
              columnWidths={["220px", "200px", "160px", "auto"]}
              enableReorder={true}
              enableDelete={true}
              enableLock={true}
              draftRowId={draftRowId}
              draftCells={draftCells}
              renderRowActions={renderRowActions}
              onDeleteRowServer={handleDeleteRowServer}
            />
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
};

export default LaboratorioDetalhesCase;
